import type { ConversationsOutcomeMetric } from "../transport"
import { MAX_INLINE_LIST_ITEMS } from "./conversationsSummaryConstants"
import type {
  CountEntry,
  ResolvedConversationsSummaryTimeframe,
} from "./conversationsSummaryTypes"

export function getTimeframeLabel(
  timeframe: ResolvedConversationsSummaryTimeframe,
): string {
  switch (timeframe) {
    case "24h":
      return "Last 24 hours"
    case "7d":
      return "Last 7 days"
    case "30d":
      return "Last 30 days"
  }
}

export function getPreviousTimeframeLabel(
  timeframe: ResolvedConversationsSummaryTimeframe,
): string {
  switch (timeframe) {
    case "24h":
      return "prior 24 hours"
    case "7d":
      return "prior 7 days"
    case "30d":
      return "prior 30 days"
  }
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    value,
  )
}

export function formatMetricNumber(value: number): string {
  const absValue = Math.abs(value)
  if (absValue >= 100 || Number.isInteger(value)) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
      value,
    )
  }
  if (absValue >= 10) {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 1,
    }).format(value)
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value)
}

export function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`
}

export function formatOutcomeMetricValue(
  metric: ConversationsOutcomeMetric,
): string {
  if (metric.currentAverage === null) {
    return "no score"
  }
  return metric.isBinary
    ? formatPercent(metric.currentAverage)
    : formatMetricNumber(metric.currentAverage)
}

export function formatDeltaPoints(delta: number): string {
  return `${Math.round(Math.abs(delta * 100))} pts`
}

export function pluralize(
  count: number,
  singular: string,
  plural = `${singular}s`,
): string {
  return count === 1 ? singular : plural
}

export function summarizeCountEntries(entries: CountEntry[]): string {
  return entries
    .slice(0, MAX_INLINE_LIST_ITEMS)
    .map((entry) => `${entry.label} (${formatCount(entry.count)})`)
    .join(", ")
}

export function formatSentenceList(items: string[]): string {
  if (items.length <= 1) {
    return items[0] ?? ""
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`
  }

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}
